import React from 'react';
import { User, Mail, FileText, MessageSquare, Send, Building2, Phone, Clock } from 'lucide-react';

// Opening hours config & helpers
export const OPENING_HOURS = [
  { day: 'Monday',    open: 10, close: 20 },
  { day: 'Tuesday',   open: 10, close: 20 },
  { day: 'Wednesday', open: 10, close: 20 },
  { day: 'Thursday',  open: 10, close: 20 },
  { day: 'Friday',    open: 10, close: 20 },
  { day: 'Saturday',  open: 10, close: 20 },
  { day: 'Sunday',    open: 10, close: 16 },
];

export const getUKStatus = () => {
  const now = new Date();
  const ukWeekday = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', weekday: 'long' }).format(now);
  const ukHour = parseInt(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: 'numeric', hour12: false }).format(now));
  const ukMinute = parseInt(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', minute: 'numeric' }).format(now));
  const hours = ukHour + ukMinute / 60;
  const dayIndex = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].indexOf(ukWeekday);
  const today = OPENING_HOURS[dayIndex];
  return { isOpen: hours >= today.open && hours < today.close, today, dayIndex };
};

// Contact Form Section - Updated with phone number
export const ContactSection = ({ 
  theme, 
  isDarkMode,
  contactForm, 
  handleContactFormChange, 
  handleContactSubmit
}) => {
  
  const handleLiveChatClick = () => {
    // Use the global function exposed by the chat widget
    if (window.openLiveChat) {
      window.openLiveChat();
    }
  };

  return (
    <div id="contact" className="mb-8 mt-16" style={{scrollMarginTop: '120px'}}>
      <div className="text-center mb-12">
        <h2 className={`section-heading section-heading-hidden text-xl sm:text-2xl ${theme.text.primary} mb-1 uppercase`}>
          Get In Touch
        </h2>
        <p className={`text-xs ${theme.text.muted} font-sans flex items-center justify-center subtitle-animate`}>
          Ready to start your next project?
        </p>
        <div className="h-0.5 mx-auto mt-2 rounded-full underline-animate" style={{backgroundColor: '#eb008b'}}></div>
      </div>

      <div className="max-w-6xl mx-auto">
        <div className={`${theme.card} backdrop-blur-sm border rounded-2xl portfolio-item`} style={{ opacity: 0 }}>
          <div className="flex flex-col md:flex-row">

            {/* Left — Form */}
            <div className={`flex-1 p-8 space-y-6 md:border-r ${isDarkMode ? 'border-slate-700/50' : 'border-gray-200'}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label htmlFor="name" className={`text-sm font-medium ${theme.text.secondary} font-sans flex items-center`}>
                  <User size={16} className="mr-2 text-pink-400" />
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={contactForm.name}
                  onChange={(e) => handleContactFormChange('name', e.target.value)}
                  className={`w-full ${theme.input} rounded-lg px-4 py-3 text-sm transition-all duration-300 font-sans focus:ring-2 focus:ring-pink-500/30 focus:border-pink-500`}
                  placeholder="Your name"
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="email" className={`text-sm font-medium ${theme.text.secondary} font-sans flex items-center`}>
                  <Mail size={16} className="mr-2 text-pink-400" />
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={contactForm.email}
                  onChange={(e) => handleContactFormChange('email', e.target.value)}
                  className={`w-full ${theme.input} rounded-lg px-4 py-3 text-sm transition-all duration-300 font-sans focus:ring-2 focus:ring-pink-500/30 focus:border-pink-500`}
                  placeholder="your.email@example.com"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label htmlFor="subject" className={`text-sm font-medium ${theme.text.secondary} font-sans flex items-center`}>
                <FileText size={16} className="mr-2 text-pink-400" />
                Subject
              </label>
              <input
                type="text"
                id="subject"
                value={contactForm.subject}
                onChange={(e) => handleContactFormChange('subject', e.target.value)}
                className={`w-full ${theme.input} rounded-lg px-4 py-3 text-sm transition-all duration-300 font-sans`}
                placeholder="What would you like to discuss?"
                required
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="message" className={`text-sm font-medium ${theme.text.secondary} font-sans flex items-center`}>
                <MessageSquare size={16} className="mr-2 text-pink-400" />
                Message
              </label>
              <textarea
                id="message"
                value={contactForm.message}
                onChange={(e) => handleContactFormChange('message', e.target.value)}
                rows="6"
                className={`w-full ${theme.input} rounded-lg px-4 py-3 text-sm transition-all duration-300 font-sans resize-none`}
                placeholder="Tell us about your project, requirements, or any questions you have..."
                onFocus={(e) => e.target.style.borderColor = '#eb008b'}
                onBlur={(e) => e.target.style.borderColor = isDarkMode ? '#475569' : '#e5e7eb'}
                required
              />
            </div>
            
            <div className="flex justify-center py-4">
              <button
                onClick={handleContactSubmit}
                className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-400 hover:to-pink-500 text-white px-6 py-2 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 flex items-center space-x-2 font-sans text-sm"
              >
                <Send size={18} />
                <span>Send Message</span>
              </button>
            </div>
            </div>{/* end left column */}

            {/* Divider — vertical on desktop, horizontal on mobile */}
            <div className={`block md:hidden border-t mx-8 ${isDarkMode ? 'border-slate-700/50' : 'border-gray-200'}`} />

            {/* Right — Contact info */}
            <div className="md:w-80 lg:w-96 p-8 flex flex-col justify-center items-center space-y-6 text-center">

              {/* 1 — Opening hours */}
              <div className="w-full">
                {(() => {
                  const { isOpen, today, dayIndex } = getUKStatus();
                  const now = new Date();
                  const ukHour = parseInt(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: 'numeric', hour12: false }).format(now));
                  const ukMinute = parseInt(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', minute: 'numeric' }).format(now));
                  const currentHour = ukHour + ukMinute / 60;
                  const days = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
                  if (isOpen) return (
                    <p className="text-green-400 text-sm font-sans font-medium mb-3 flex items-center justify-center gap-1.5">
                      <Clock size={14} /> Open now — until {today.close === 20 ? '8pm' : today.close === 16 ? '4pm' : `${today.close}pm`}
                    </p>
                  );
                  if (currentHour < today.open) return (
                    <p className={`${theme.text.muted} text-sm font-sans mb-3 flex items-center justify-center gap-1.5`}>
                      <Clock size={14} /> Opening today at {today.open === 10 ? '10am' : `${today.open}am`}
                    </p>
                  );
                  const nextIndex = (dayIndex + 1) % 7;
                  const nextDay = OPENING_HOURS[nextIndex];
                  const label = (dayIndex + 1) % 7 === nextIndex ? 'tomorrow' : days[nextIndex];
                  return (
                    <p className={`${theme.text.muted} text-sm font-sans mb-3 flex items-center justify-center gap-1.5`}>
                      <Clock size={14} /> Opening {label} at {nextDay.open === 10 ? '10am' : `${nextDay.open}am`}
                    </p>
                  );
                })()}
                <div className="inline-grid grid-cols-2 gap-x-6 gap-y-1 text-sm font-sans mx-auto">
                  {(() => {
                    const now = new Date();
                    const ukWeekday = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', weekday: 'long' }).format(now);
                    const todayIndex = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].indexOf(ukWeekday);
                    return OPENING_HOURS.map(({ day, open, close }, i) => {
                      const isToday = todayIndex === i;
                      return (
                        <React.Fragment key={day}>
                          <span className={isToday ? 'text-pink-400 font-semibold' : theme.text.secondary}>{day}</span>
                          <span className={isToday ? 'text-pink-400 font-semibold' : theme.text.secondary}>
                            {open === 10 ? '10am' : `${open}am`} – {close === 20 ? '8pm' : close === 16 ? '4pm' : `${close}pm`}
                          </span>
                        </React.Fragment>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* 2 — Contact links */}
              <div className={`w-full border-t ${isDarkMode ? 'border-slate-700/50' : 'border-gray-200'} pt-6`}>
                <p className={`${theme.text.muted} text-sm font-sans mb-4`}>Prefer to reach out directly?</p>
                <div className="flex flex-col items-center space-y-3">
                  <button
                    onClick={handleLiveChatClick}
                    className={`flex items-center space-x-2 ${theme.text.secondary} hover:text-pink-400 transition-colors font-sans text-sm`}
                  >
                    <MessageSquare size={16} className="text-pink-400" />
                    <span>Live Chat</span>
                  </button>
                  <a href="tel:02084021384" className={`flex items-center space-x-2 ${theme.text.secondary} hover:text-pink-400 transition-colors font-sans text-sm`}>
                    <Phone size={16} className="text-pink-400" />
                    <span>020 8402 1384</span>
                  </a>
                  <a href="mailto:info@spiralcreations.co.uk" className={`flex items-center space-x-2 ${theme.text.secondary} hover:text-pink-400 transition-colors font-sans text-sm`}>
                    <Mail size={16} className="text-pink-400" />
                    <span>info@spiralcreations.co.uk</span>
                  </a>
                </div>
              </div>

              {/* 3 — Address */}
              <div className={`w-full border-t ${isDarkMode ? 'border-slate-700/50' : 'border-gray-200'} pt-6 pb-2`}>
                <div className={`${theme.text.secondary} font-sans text-sm`}>
                  <p className="flex items-center justify-center">
                    <Building2 size={16} className="mr-2 text-pink-400" />
                    1b Kelsey Park Road, London, BR3 6LH
                  </p>
                  <p className="text-pink-400 text-xs mt-2 text-center">Visits to the studio are by appointment only</p>
                </div>
              </div>

            </div>{/* end right column */}
          </div>{/* end flex row */}
        </div>
      </div>
    </div>
  );
};

// Footer Component
export const Footer = ({ theme, isDarkMode }) => {
  return (
    <footer className={`${isDarkMode ? 'bg-slate-800/60' : 'bg-gray-200/90'} backdrop-blur-sm border-t ${isDarkMode ? 'border-slate-700/50' : 'border-gray-300'} py-4 relative z-10`}>
      <div className="text-center">
        <div className="flex items-center justify-center mx-auto mb-2">
          <img 
            src="/sc_logo_notext.svg"
            alt="Spiral Creations" 
            className="h-4 w-auto"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div 
            className={`w-4 h-4 ${isDarkMode ? 'bg-white' : 'bg-slate-800'} rounded-lg`}
            style={{ display: 'none', alignItems: 'center', justifyContent: 'center', flexDirection: 'row' }}
          >
            <div className="w-2 h-2 bg-pink-600 rounded-sm"></div>
          </div>
        </div>
        <p className={`${theme.text.muted} text-sm font-sans`}>© 2026 Spiral Creations</p>
        <div className="flex items-center justify-center space-x-3 mt-1">
          <span className={`${theme.text.muted} text-xs font-sans`}>Privacy Policy</span>
          <span className={`${theme.text.muted} text-xs`}>•</span>
          <span className={`${theme.text.muted} text-xs font-sans`}>Cookie Policy</span>
        </div>
      </div>
    </footer>
  );
};